import React, { useState } from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';

const LABEL = {
  technology: 'technology',
  equipment: 'equipment',
  furniture: 'furniture',
  appliances: 'appliances',
  vehicle: 'vehicle-related item',
};

function money(v) {
  if (v == null || Number.isNaN(Number(v))) return '';
  return Number(v).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

/**
 * AssetBanner has two modes.
 *
 *  1) `asset_suggestion.status === 'pending'`  → AI-driven banner (Phase 26C)
 *     with line-item list + three actions: Confirm / Reassign / Dismiss.
 *
 *  2) legacy `potential_asset`+`asset_type`    → the pre-26C warn banner with
 *     Move / Dismiss buttons.
 */
export default function AssetBanner({ data, colors, onMove, onDismiss, onConfirm, onReassign }) {
  const sug = data?.asset_suggestion;
  const aiPending = sug && sug.status === 'pending';
  const legacyActive = data?.potential_asset && !data?.asset_dismissed && !aiPending;

  if (!aiPending && !legacyActive) return null;

  // -------------------------------------------------------------------------
  // AI suggestion (Phase 26C)
  // -------------------------------------------------------------------------
  if (aiPending) {
    return <AiSuggestionBanner data={data} colors={colors} onConfirm={onConfirm} onReassign={onReassign} onDismiss={onDismiss} />;
  }

  // -------------------------------------------------------------------------
  // Legacy banner
  // -------------------------------------------------------------------------
  const label = LABEL[data.asset_type] || 'capital purchase';
  return (
    <div
      data-testid="asset-banner"
      className="rounded-xl border p-4 mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      style={{ backgroundColor: colors.warningSoft, borderColor: colors.warning, color: colors.text }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} style={{ color: colors.warning, flexShrink: 0, marginTop: 2 }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.text }}>
            This looks like a {label}.
          </p>
          <p className="text-xs mt-0.5" style={{ color: colors.text }}>
            Review whether it should be an Asset instead of an Expense.
          </p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          data-testid="asset-move-btn"
          onClick={onMove}
          className="px-3 py-1.5 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: colors.warning }}
        >
          Move to Assets
        </button>
        <button
          data-testid="asset-dismiss-btn"
          onClick={onDismiss}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border"
          style={{ borderColor: colors.warning, color: colors.warning, backgroundColor: 'transparent' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function AiSuggestionBanner({ data, colors, onConfirm, onReassign, onDismiss }) {
  const [busy, setBusy] = useState(null); // 'confirm' | 'reassign' | 'dismiss' | null
  const sug = data.asset_suggestion || {};
  const items = Array.isArray(sug.items) ? sug.items : [];
  const overall = sug.overall_reason || '';

  async function run(kind, fn) {
    if (busy) return;
    setBusy(kind);
    try { await fn?.(); } finally { setBusy(null); }
  }

  return (
    <div
      data-testid="asset-suggestion-banner"
      className="rounded-xl border p-4 mb-4"
      style={{ backgroundColor: colors.accentSoft, borderColor: colors.accent }}
    >
      <div className="flex items-start gap-3">
        <Sparkles size={18} style={{ color: colors.accent, flexShrink: 0, marginTop: 2 }} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold" style={{ color: colors.text }}>
            AI thinks this looks like a business asset.
          </p>
          {overall && (
            <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
              {overall}
            </p>
          )}
          {items.length > 0 && (
            <ul className="mt-2 space-y-1">
              {items.map((it, i) => (
                <li key={i} className="text-xs flex items-baseline gap-2" style={{ color: colors.text }}>
                  <span className="font-semibold">{it.name}</span>
                  {it.amount != null && (
                    <span className="tabular-nums" style={{ color: colors.textMuted }}>{money(it.amount)}</span>
                  )}
                  {it.reason && (
                    <span className="truncate" style={{ color: colors.textMuted }}>— {it.reason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 justify-end">
        <button
          data-testid="asset-ai-confirm-btn"
          onClick={() => run('confirm', onConfirm)}
          disabled={busy !== null}
          className="px-3 py-1.5 rounded-full text-xs font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: colors.accent }}
        >
          {busy === 'confirm' ? 'Working…' : 'Confirm as Asset'}
        </button>
        <button
          data-testid="asset-ai-reassign-btn"
          onClick={() => run('reassign', onReassign)}
          disabled={busy !== null}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50"
          style={{ borderColor: colors.accent, color: colors.accent, backgroundColor: 'transparent' }}
        >
          Reassign
        </button>
        <button
          data-testid="asset-ai-dismiss-btn"
          onClick={() => run('dismiss', onDismiss)}
          disabled={busy !== null}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50"
          style={{ borderColor: colors.border, color: colors.textMuted, backgroundColor: 'transparent' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
